import type { FeatureToggles, EntityTypes } from 'api/data.types'
import type { UserState } from 'utils/hooks/use-userInfo'
import {
  ADMIN_ACCESS_REPORT_PATH,
  ADMIN_ACTIVITIES_HOME_PATH,
  ADMIN_ACTIVITIES_HOME_PATH_V2,
  ADMIN_ACTIVITIES_REPORT_PATH,
  ADMIN_BOOK_INDICATION_PATH,
  ADMIN_ENTITY_USERS_ONLINE_STATUS_PATH_V2,
  ADMIN_ENTITY_USERS_V2_PATH,
  ADMIN_JOB_POSITIONS_AND_PERMISSIONS,
  ADMIN_LEAGUE_REPORT_V2,
  ADMIN_READING_ASSESSMENT_HOME_V2,
  ADMIN_READING_FLUENCY_APP_V2,
  ADMIN_READING_GOALS_REPORT_PATH,
  ADMIN_REPORTS_PATH,
  ADMIN_RUNNING_RECORDS_REPORT_PATH,
  ADMIN_SUMMARY_REPORT_V2_PATH,
  READING_REPORTS_V2_PATH
} from 'routes/paths'
import type { DropdownItemProps } from './types'
import type { LanguageTokens } from 'i18n'
import { canAccessEducatorReports } from 'screens/admin-v2/reports-v2/educator-reports/utils'

export const dropdownItemsTimeUsage = (
  featureToggles: FeatureToggles,
  t: (text: LanguageTokens) => string
): DropdownItemProps[] => {
  const { paranaNotice, reportReadingGoalsEnabled, entityUsersOnlineStatus } =
    featureToggles

  const items = [
    paranaNotice && {
      title: t('Relatório de Acesso'),
      path: ADMIN_ACCESS_REPORT_PATH
    },
    entityUsersOnlineStatus && {
      title: t('Relatório de Status'),
      path: ADMIN_ENTITY_USERS_ONLINE_STATUS_PATH_V2
    },
    reportReadingGoalsEnabled && {
      title: t('Metas de Leitura'),
      path: ADMIN_READING_GOALS_REPORT_PATH
    }
  ]

  return items.filter(Boolean)
}

export const dropdownItemsPlanning = (
  user: UserState,
  t: (text: LanguageTokens) => string
): DropdownItemProps[] => {
  const { canAccessLeagueContent, featureToggles, canAccessIndications } = user
  const { reportReadingGoalsEnabled, adminPanelV2Rollout } = featureToggles
  const items = [
    reportReadingGoalsEnabled && {
      title: t('Metas de Leitura'),
      path: ADMIN_READING_GOALS_REPORT_PATH
    },
    canAccessLeagueContent && {
      title: t('Liga de Leitores'),
      path: ADMIN_LEAGUE_REPORT_V2
    },
    !adminPanelV2Rollout &&
      canAccessIndications && {
        title: t('Indicar livro'),
        path: ADMIN_BOOK_INDICATION_PATH
      }
  ]

  return items.filter(Boolean)
}

export const dropdownItemsAssessments = (
  user: UserState,
  featureToggles: FeatureToggles,
  t: (text: LanguageTokens) => string
): DropdownItemProps[] => {
  const { canAccessReadingFluency, canAccessActivities } = user
  const {
    doActivityV2,
    paranaNotice,
    activitiesV3,
    storyTree,
    runningRecords,
    readAloudV2Leveling
  } = featureToggles

  const shouldShowNewCatalog = activitiesV3
  const catalogTitle = shouldShowNewCatalog
    ? t('Catálogo de atividades')
    : t('Atividades e Tarefas')

  const readingFluencyPath = readAloudV2Leveling
    ? ADMIN_READING_FLUENCY_APP_V2
    : ADMIN_READING_ASSESSMENT_HOME_V2

  const items = [
    canAccessReadingFluency && {
      title: t('Fluência leitora'),
      path: readingFluencyPath
    },
    canAccessActivities &&
      !doActivityV2 &&
      !paranaNotice &&
      !shouldShowNewCatalog && {
        title: t('Atividades e Tarefas'),
        path: ADMIN_ACTIVITIES_HOME_PATH
      },
    canAccessActivities &&
      (doActivityV2 || shouldShowNewCatalog) &&
      !paranaNotice && {
        title: catalogTitle,
        path: ADMIN_ACTIVITIES_HOME_PATH_V2
      },
    (storyTree || runningRecords) && {
      title: t('Running Records'),
      path: ADMIN_RUNNING_RECORDS_REPORT_PATH
    }
  ]

  return items.filter(Boolean)
}

export const dropdownItemsPerformance = (
  user: UserState,
  t: (text: LanguageTokens) => string
): DropdownItemProps[] => {
  const { canAccessUsageReports, canAccessLeagueContent, featureToggles } = user

  const items = [
    canAccessUsageReports && {
      title: t('Relatório de Leitura'),
      path:
        featureToggles.paranaNotice && featureToggles.readingReportV2
          ? READING_REPORTS_V2_PATH
          : [
              ADMIN_REPORTS_PATH + '#turmas',
              ADMIN_REPORTS_PATH + '#usuarios',
              ADMIN_REPORTS_PATH + '#livros-lidos'
            ]
    },
    canAccessUsageReports && {
      title: t('Relatório de Indicações'),
      path: ADMIN_REPORTS_PATH + '#indicados'
    },
    canAccessUsageReports && {
      title: t('Relatório de Atividades'),
      path: ADMIN_ACTIVITIES_REPORT_PATH
    },
    canAccessLeagueContent && {
      title: t('Relatório da Liga'),
      path: ADMIN_LEAGUE_REPORT_V2
    }
  ]

  return items.filter(Boolean)
}

export const dropdownItemsManage = (
  user: UserState,
  canManagePermissions: boolean,
  t: (text: LanguageTokens) => string,
  isSuperAutorEligible?: boolean,
  unifyRolesAndPermissions?: boolean
): DropdownItemProps[] => {
  const { canAccessEntityUsersManagement } = user

  if (user.featureToggles.doActivityV2) {
    const items = [
      (user.isAdmin ||
        (canAccessEntityUsersManagement && !isSuperAutorEligible)) && {
        title: t('Grupos e Usuários'),
        path: ADMIN_ENTITY_USERS_V2_PATH
      },
      canManagePermissions &&
        !unifyRolesAndPermissions && {
          title: t('Cargos e Permissões'),
          path: ADMIN_JOB_POSITIONS_AND_PERMISSIONS
        }
    ]

    return items.filter(Boolean)
  }

  const items = [
    canAccessEntityUsersManagement &&
      (!isSuperAutorEligible || user.isAdmin) && {
        title: t('Grupos e Usuários'),
        path: ADMIN_ENTITY_USERS_V2_PATH
      },
    canManagePermissions &&
      !unifyRolesAndPermissions && {
        title: t('Cargos e Permissões'),
        path: ADMIN_JOB_POSITIONS_AND_PERMISSIONS
      }
  ]

  return items.filter(Boolean)
}

export const dropdownItemsReportsDoActivityV2 = (
  user: UserState,
  featureToggles: FeatureToggles,
  t: (text: LanguageTokens) => string,
  hasTeacherId?: boolean
): DropdownItemProps[] => {
  const userEntityType = user?.reader?.entity?.type

  const items = [
    {
      path: `${ADMIN_SUMMARY_REPORT_V2_PATH}#visao-geral`,
      title: t('Visão Geral')
    },
    {
      path: `${ADMIN_SUMMARY_REPORT_V2_PATH}#livros`,
      title: t('Livros')
    },
    {
      path: `${ADMIN_SUMMARY_REPORT_V2_PATH}#atividades`,
      title: t('Atividades')
    },
    user?.featureToggles?.activitiesV3 && {
      path: `${ADMIN_SUMMARY_REPORT_V2_PATH}#activitiesV3`,
      title: t('Atividades')
    },
    {
      path: `${ADMIN_SUMMARY_REPORT_V2_PATH}#tarefas`,
      title: t('Tarefas')
    },
    {
      path: `${ADMIN_SUMMARY_REPORT_V2_PATH}#acessos`,
      title: t('Acessos')
    },
    canAccessEducatorReports(userEntityType as EntityTypes, hasTeacherId) && {
      path: `${ADMIN_SUMMARY_REPORT_V2_PATH}#educadores`,
      title: t('Educadores')
    },
    user?.teacher?.root?.requirePayment && {
      path: `${ADMIN_SUMMARY_REPORT_V2_PATH}#pagamentos`,
      title: t('Pagamentos')
    }
  ]

  return items.filter(Boolean)
}

export const dropdownItemsReportsParanaNotice = (
  t: (text: LanguageTokens) => string,
  user?: UserState
): DropdownItemProps[] => {
  const items = [
    {
      path: `${ADMIN_SUMMARY_REPORT_V2_PATH}#visao-geral`,
      title: t('Visão Geral')
    },
    {
      path: `${ADMIN_SUMMARY_REPORT_V2_PATH}#livros`,
      title: t('Livros')
    },
    {
      path: `${ADMIN_SUMMARY_REPORT_V2_PATH}#atividades`,
      title: t('Atividades')
    },
    user?.featureToggles?.activitiesV3 && {
      path: `${ADMIN_SUMMARY_REPORT_V2_PATH}#activitiesV3`,
      title: t('Atividades')
    }
  ]

  return items.filter(Boolean)
}

export const dropdownItemsReportsV2 = (
  user: UserState,
  featureToggles: FeatureToggles,
  t: (text: LanguageTokens) => string,
  hasTeacherId?: boolean
): DropdownItemProps[] => {
  const requirePayment = user?.teacher?.root?.requirePayment
  const userEntityType = user?.reader?.entity?.type

  const items = [
    {
      path: `${ADMIN_SUMMARY_REPORT_V2_PATH}#visao-geral`,
      title: t('Visão Geral')
    },
    {
      path: `${ADMIN_SUMMARY_REPORT_V2_PATH}#livros`,
      title: featureToggles?.hideNews ? t('Livros') : t('Livros e Notícias')
    },
    {
      path: `${ADMIN_SUMMARY_REPORT_V2_PATH}#indicacoes`,
      title: t('Indicações')
    },
    user?.featureToggles?.activitiesV3 && {
      path: `${ADMIN_SUMMARY_REPORT_V2_PATH}#activitiesV3`,
      title: t('Atividades')
    },
    {
      path: `${ADMIN_SUMMARY_REPORT_V2_PATH}#acessos`,
      title: t('Acessos')
    },
    featureToggles?.storyTree && {
      path: `${ADMIN_SUMMARY_REPORT_V2_PATH}#jornadas`,
      title: t('Jornadas')
    },
    canAccessEducatorReports(userEntityType as EntityTypes, hasTeacherId) && {
      path: `${ADMIN_SUMMARY_REPORT_V2_PATH}#educadores`,
      title: t('Educadores')
    },
    requirePayment && {
      path: `${ADMIN_SUMMARY_REPORT_V2_PATH}#pagamentos`,
      title: t('Pagamentos')
    }
  ]

  return items.filter(Boolean)
}
